import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Segment } from '../models/Segment';
import SpeakerSelectionMenu from './SpeakerSelectionMenu';
import SpeakerFormModal from './modals/SpeakerFormModal';
import { TranscriptVoiceMapping } from '../types';
import { TranscriptEditorStore } from '../TranscriptEditorStore';
import { SpeakerEvent } from '../services/interfaces/ISpeakerService';

/**
 * SPEAKER LABEL VS SPEAKER ID REFERENCE
 * 
 * This component deals with two types of speaker identifiers:
 * 
 * 1. Speaker Labels: Used in transcript segments, always in "spk_X" format
 *    - segment.speakerLabel contains this anonymous speaker label
 *    - These are internal identifiers that should never be shown to users
 * 
 * 2. Speaker IDs: DatoCMS speaker IDs, obtained through the mapping system
 *    - speakerMappings.mappings[speakerLabel] gives the real Dato ID
 *    - These Dato IDs are used to look up display names
 * 
 * The proper flow for displaying speaker names:
 * 1. Get the speaker label from segment.speakerLabel
 * 2. Look up the Dato speaker ID in the mappings
 * 3. Find the Dato speaker by ID in the datoSpeakers array
 * 4. Display the name property from the Dato speaker
 * 
 * See also comprehensive documentation in:
 * - /transcript/services/SpeakerService.ts
 * - /transcript/models/TranscriptSpeaker.ts
 */

interface SegmentSpeakerDropdownProps {
  segment: Segment;
  editorStore: TranscriptEditorStore;
  color?: string; // Optional fallback color if speaker service doesn't provide one
}

/**
 * Dropdown component for selecting a speaker for a segment
 * Uses the SpeakerSelectionMenu component for consistent UI with text selection
 * 
 */
const SegmentSpeakerDropdown = observer((props: SegmentSpeakerDropdownProps) => {
  const { segment, editorStore, color = '#A9A9A9' } = props;
  // Menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showNewSpeakerModal, setShowNewSpeakerModal] = useState(false);
  const [speakerName, setSpeakerName] = useState<string>('...');
  const [speakerColor, setSpeakerColor] = useState<string>(color);
  
  // Update speaker name and color when segment changes
  useEffect(() => {
    if (segment && editorStore?.editor?.speakerService) {
      // Function to update the speaker name and color
      const updateSpeakerInfo = () => {
        if (editorStore.editor?.speakerService) {
          const name = editorStore.editor?.speakerService?.getSpeakerName(segment.speakerLabel) || '...';
          const newColor = editorStore.editor?.speakerService?.getSpeakerColor(segment.speakerLabel) || color;

          setSpeakerName(name);
          setSpeakerColor(newColor);
        } else{
          setSpeakerName('...');
          setSpeakerColor(color);
        }
      };
      
      // Subscribe to mapping updates
      editorStore.editor.speakerService.on(SpeakerEvent.MAPPINGS_UPDATED, updateSpeakerInfo);
      
      // Initial update
      updateSpeakerInfo();
      
      // Cleanup: unsubscribe when component unmounts or dependencies change
      return () => {
        if (editorStore?.editor?.speakerService) {
            editorStore.editor.speakerService.off(SpeakerEvent.MAPPINGS_UPDATED, updateSpeakerInfo);
        }
      };
    }
  }, [segment, segment.speakerLabel, editorStore, color]);
  

  // Handle opening the dropdown
  const handleOpenMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // First, select the segment in the selection service
    if (editorStore.editor?.selectionService) {
      editorStore.editor.selectionService.clearAllSelections();
      editorStore.editor.selectionService.setSelectedSegment(segment);
    }

    // Position the menu below the dropdown
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const dropdownWidth = 250;
      const dropdownHeight = 370;

      const rectLeft = rect.left;
      const rectBottom = rect.bottom;

      const x = Math.min(rectLeft, windowWidth - dropdownWidth);
      const y = Math.min(rectBottom + 5, windowHeight - dropdownHeight);

      const menuPosition = {
        x,
        y
      };

      setMenuPosition(menuPosition);
    }

    setMenuOpen(true);
  };

  // Handle closing the dropdown
  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  // Handle new speaker creation
  const handleCreateNewSpeaker = () => {
    // Show the speaker creation modal when the "New Speaker" button is clicked
    setShowNewSpeakerModal(true);
    // Close the speaker selection menu immediately
    setMenuOpen(false);
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton 
        onClick={handleOpenMenu}
        color={speakerColor}
      >
        {speakerName || 'Unknown'}
      </DropdownButton>
      
      {menuOpen && (
        <SpeakerSelectionMenu
          key={`segment-speaker-menu-${segment.id}`}
          isOpen={menuOpen}
          position={menuPosition}
          editorStore={editorStore}
          onClose={handleCloseMenu}
          onCreateNewSpeaker={handleCreateNewSpeaker}
          menuTitle={`Assign Speaker to Segment`}
        />
      )}

      {/* Modal for creating new speakers */}
      {showNewSpeakerModal && (
        <SpeakerFormModal
          key={`segment-new-speaker-modal-${segment.id}`}
          isOpen={showNewSpeakerModal}
          onClose={() => {
            setShowNewSpeakerModal(false);
            // Ensure menu stays closed after new speaker creation
            setMenuOpen(false);
          }}
          editorStore={editorStore}
        />
      )}
    </DropdownContainer>
  );
});

// Styled components
const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button<{ color: string }>`
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.color};
  border-radius: 4px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:focus {
    outline: none;
  }
`;

export default SegmentSpeakerDropdown;